import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Upload, User, Trash2, FolderOpen, Search, Check, Loader2, Image } from 'lucide-react';
import { uploadApi } from '../api/client';
import type { MediaItem } from '../api/modules/upload';

/**
 * Format bytes to human-readable string
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

interface TeamUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: string;
  memberId: string;
  memberName: string;
  currentImageUrl?: string;
  onImageUploaded: (imageUrl: string, cloudfrontUrl?: string, mediaId?: string) => void;
}

export const TeamUploadModal: React.FC<TeamUploadModalProps> = ({
  isOpen,
  onClose,
  siteId,
  memberId,
  memberName,
  currentImageUrl,
  onImageUploaded,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<'upload' | 'library'>('upload');

  // Library state
  const [libraryMedia, setLibraryMedia] = useState<MediaItem[]>([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLibraryMedia, setSelectedLibraryMedia] = useState<MediaItem | null>(null);

  // Filter library media based on search (images only for team members)
  const filteredLibraryMedia = useMemo(() => {
    return libraryMedia.filter(item => {
      // Only show images
      if (!item.mimeType.startsWith('image/')) return false;
      // Filter by search query
      if (searchQuery) {
        return item.filename.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return true;
    });
  }, [libraryMedia, searchQuery]);

  // Fetch library media when switching to library tab
  useEffect(() => {
    if (activeTab === 'library' && isOpen) {
      const fetchLibraryMedia = async () => {
        setIsLoadingLibrary(true);
        setLibraryError(null);
        try {
          const media = await uploadApi.getMedia(siteId);
          setLibraryMedia(media);
        } catch (err) {
          console.error('Failed to fetch library media:', err);
          setLibraryError('Failed to load media library');
        } finally {
          setIsLoadingLibrary(false);
        }
      };
      fetchLibraryMedia();
    }
  }, [activeTab, isOpen, siteId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('upload');
      setSelectedLibraryMedia(null);
      setSearchQuery('');
      setError(null);
      setLibraryError(null);
    }
  }, [isOpen]);

  // Handle selecting from library
  const handleSelectFromLibrary = () => {
    if (selectedLibraryMedia) {
      const imageUrl = selectedLibraryMedia.cloudfrontUrl || selectedLibraryMedia.url;
      onImageUploaded(imageUrl, selectedLibraryMedia.cloudfrontUrl, selectedLibraryMedia.id);
      onClose();
    }
  };

  const handleFileSelect = (file: File) => {
    setError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size must be less than 10MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = e => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const response = await uploadApi.uploadTeamMemberImage(file, siteId, memberId, memberName);

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Update the parent component with the new image URL
      onImageUploaded(response.data.url, response.data.cloudfrontUrl, response.data.id);

      // Close modal after a brief delay to show completion
      setTimeout(() => {
        onClose();
        setUploadProgress(0);
        setPreviewUrl(null);
      }, 500);
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setPreviewUrl(null);
    onImageUploaded(''); // Pass empty string to remove image
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg h-[80vh] flex flex-col border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Team Member Photo</h2>
            <p className="text-sm text-gray-500 mt-1">Upload a photo for {memberName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            disabled={isUploading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'upload'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Upload size={16} />
            <span>Upload New</span>
          </button>
          <button
            onClick={() => setActiveTab('library')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'library'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FolderOpen size={16} />
            <span>Choose from Library</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Current/Preview Image */}
              {previewUrl && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Photo
                  </label>
                  <div className="relative inline-block">
                    <img
                      src={previewUrl}
                      alt={`${memberName} photo`}
                      className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                    />
                    <button
                      onClick={handleRemoveImage}
                      className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      title="Remove photo"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              )}

              {/* Upload Area */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  {previewUrl ? 'Change Photo' : 'Upload Photo'}
                </label>

                <div
                  className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {isUploading ? (
                    <div className="space-y-4">
                      <div className="mx-auto w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                        <Upload size={24} className="text-primary-600 animate-pulse" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Uploading...</p>
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{uploadProgress}%</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <User size={24} className="text-gray-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Drop an image here, or{' '}
                          <button
                            onClick={handleBrowseClick}
                            className="text-primary-600 hover:text-primary-700 font-medium"
                          >
                            browse
                          </button>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">PNG, JPG, or WebP up to 10MB</p>
                      </div>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                    disabled={isUploading}
                  />
                </div>
              </div>

              {/* Tips */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Tips for best results:</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Use a square image (1:1 aspect ratio) for best results</li>
                  <li>• Minimum recommended size: 300x300 pixels</li>
                  <li>• Images will be automatically optimized and resized</li>
                  <li>• Consider using a professional headshot</li>
                </ul>
              </div>
            </>
          )}

          {/* Library Tab */}
          {activeTab === 'library' && (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search images..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Library Content */}
              {isLoadingLibrary ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 size={32} className="text-primary-600 animate-spin mb-4" />
                  <p className="text-gray-500">Loading media library...</p>
                </div>
              ) : libraryError ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <p className="text-red-500 mb-2">{libraryError}</p>
                  <button
                    onClick={() => setActiveTab('library')}
                    className="text-primary-600 hover:text-primary-700 text-sm"
                  >
                    Try again
                  </button>
                </div>
              ) : filteredLibraryMedia.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <FolderOpen size={48} className="text-gray-300 mb-4" />
                  <p className="text-gray-700 font-medium mb-2">No images found</p>
                  <p className="text-gray-500 text-sm">
                    {searchQuery
                      ? 'Try a different search term'
                      : 'Upload some images to get started'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {filteredLibraryMedia.map(item => {
                    const selected = selectedLibraryMedia?.id === item.id;
                    return (
                      <div
                        key={item.id}
                        onClick={() => setSelectedLibraryMedia(item)}
                        className={`group relative aspect-square rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${
                          selected
                            ? 'border-primary-500 ring-2 ring-primary-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={item.cloudfrontUrl || item.url}
                          alt={item.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {/* Selection indicator */}
                        {selected && (
                          <div className="absolute inset-0 bg-primary-500/20 flex items-center justify-center">
                            <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                              <Check size={18} className="text-white" />
                            </div>
                          </div>
                        )}
                        {/* Type badge */}
                        <div className="absolute top-1 left-1">
                          <Image size={12} className="text-white drop-shadow-lg" />
                        </div>
                        {/* Filename tooltip on hover */}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-xs text-white truncate">{item.filename}</p>
                          <p className="text-xs text-white/70">{formatFileSize(item.size)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
              disabled={isUploading}
            >
              Cancel
            </button>
            {activeTab === 'library' && (
              <button
                onClick={handleSelectFromLibrary}
                disabled={!selectedLibraryMedia}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Use Selected
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
